/**
 * GoHighLevel — Calendar Appointments TypeBox schemas (Story 7.1).
 *
 * Validated wire shapes for the GHL `/calendars/events/appointments` and
 * `/calendars/:calendarId/free-slots` endpoints. Doc cross-reference:
 *   - `docs/architecture/ghl-calendar-integration.md` §1 (endpoints)
 *   - `https://marketplace.gohighlevel.com/docs/ghl/calendars/create-appointment/`
 *   - `https://marketplace.gohighlevel.com/docs/ghl/calendars/get-slots/`
 *
 * Constitution Article IV (No Invention): every field below traces to the
 * official GHL doc. We project a useful subset and keep
 * `additionalProperties: true` on response shapes so a GHL release that adds
 * fields never breaks our parser.
 *
 * `additionalProperties: true` on requests is intentional too — GHL accepts
 * extra optional fields (notes, customFields, etc.) the SDR flow may want to
 * add later without re-validating.
 */
import { Type } from '@sinclair/typebox';
// ============================================================================
// Free slots — GET /calendars/:calendarId/free-slots
// ============================================================================
/**
 * Single free slot. GHL returns slots inside a date-keyed map (see
 * `GHLFreeSlotsResponse` below); the `start`/`end` are ISO-8601 strings with
 * timezone offsets (e.g. `2026-05-09T13:00:00-03:00`).
 *
 * The service flattens the by-date map into a flat list of `{ start, end }`
 * Date pairs for downstream consumption (matches the `CalendarService` Google
 * interface that the SDR worker already speaks).
 */
export const GHLFreeSlot = Type.Object({
    start: Type.String({ minLength: 1, description: 'ISO-8601 start time with offset' }),
    end: Type.String({ minLength: 1, description: 'ISO-8601 end time with offset' }),
}, { additionalProperties: true });
/**
 * GHL `/free-slots` response shape — an availability map keyed by date in
 * `YYYY-MM-DD` form, plus non-date meta keys (e.g. `traceId`).
 *
 * Validated against the live GHL V2 API (2026-06): the **real** shape wraps a
 * list of bare ISO start-time strings under a `slots` key:
 *   - `{ "YYYY-MM-DD": { slots: ["2026-05-09T13:00:00-03:00", ...] } }` ← real
 *
 * Two earlier *assumed* shapes are still accepted by the service for safety:
 *   - `Array<{ slot: GHLFreeSlot }>` — assumed/newer
 *   - `Array<GHLFreeSlot>`           — assumed/legacy
 *
 * The service's `flattenSlotsByDate` helper handles all of them defensively
 * and derives each slot's `end` from the configured slot duration when GHL
 * only returns start times.
 *
 * `additionalProperties: true` is required at the top level: the keys are
 * dynamic dates (plus meta keys), so TypeBox cannot enumerate them ahead of
 * time. We validate shape only at the value level.
 */
export const GHLFreeSlotsResponse = Type.Record(Type.String(), Type.Union([
    // Real GHL V2 shape: { slots: ["ISO", ...] }
    Type.Object({ slots: Type.Array(Type.String()) }, { additionalProperties: true }),
    Type.Array(Type.Object({ slot: GHLFreeSlot }, { additionalProperties: true })),
    Type.Array(GHLFreeSlot),
    // Meta keys (e.g. traceId) — string values the service ignores.
    Type.String(),
]), {
    additionalProperties: true,
    description: 'GHL free-slots availability map keyed by YYYY-MM-DD',
});
// ============================================================================
// Appointment — POST/PUT/DELETE/GET /calendars/events/appointments
// ============================================================================
/**
 * Appointment status values GHL emits. We project the ones the SDR flow
 * cares about; `additionalProperties: true` on the parent shape covers any
 * GHL-side additions.
 */
export const GHLAppointmentStatus = Type.Union([
    Type.Literal('new'),
    Type.Literal('confirmed'),
    Type.Literal('cancelled'),
    Type.Literal('showed'),
    Type.Literal('noshow'),
    Type.Literal('invalid'),
], { description: 'GHL appointment lifecycle status' });
/**
 * Body shape accepted by `POST /calendars/events/appointments`.
 *
 * Required fields per GHL doc: `calendarId`, `locationId`, `contactId`,
 * `startTime`, `endTime`. We expose `assignedUserId` because the SDR flow
 * maps it from `sellers.ghl_user_id` (Story 3.6 schema column).
 */
export const GHLAppointmentCreateRequest = Type.Object({
    calendarId: Type.String({ minLength: 1 }),
    locationId: Type.String({ minLength: 1 }),
    contactId: Type.String({ minLength: 1 }),
    startTime: Type.String({
        minLength: 1,
        description: 'ISO-8601 with offset, e.g. 2026-05-09T13:00:00-03:00',
    }),
    endTime: Type.String({ minLength: 1 }),
    title: Type.Optional(Type.String()),
    appointmentStatus: Type.Optional(GHLAppointmentStatus),
    assignedUserId: Type.Optional(Type.String()),
    address: Type.Optional(Type.String()),
    description: Type.Optional(Type.String()),
    /** Free-form `meta` per GHL doc — used for SDR-side correlation. */
    meta: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
}, { additionalProperties: true });
/**
 * Body shape accepted by `PUT /calendars/events/appointments/:id`. All fields
 * optional — GHL applies a partial update over the existing appointment.
 */
export const GHLAppointmentUpdateRequest = Type.Object({
    calendarId: Type.Optional(Type.String()),
    startTime: Type.Optional(Type.String()),
    endTime: Type.Optional(Type.String()),
    title: Type.Optional(Type.String()),
    appointmentStatus: Type.Optional(GHLAppointmentStatus),
    assignedUserId: Type.Optional(Type.String()),
    address: Type.Optional(Type.String()),
    description: Type.Optional(Type.String()),
    meta: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
}, { additionalProperties: true });
/**
 * Appointment record returned by `POST/PUT/GET /calendars/events/appointments`.
 *
 * The SDR layer reads `id` (canonical key), `calendarId`, `contactId`,
 * `startTime`/`endTime` (for `meeting.scheduled_at` mapping), and
 * `appointmentStatus` (for `outcome` mapping). Other fields are preserved
 * via `additionalProperties: true` so the webhook (Story 7.2) can use the
 * same projection.
 */
export const GHLAppointment = Type.Object({
    id: Type.String({ minLength: 1 }),
    calendarId: Type.String({ minLength: 1 }),
    locationId: Type.Optional(Type.String()),
    contactId: Type.String({ minLength: 1 }),
    startTime: Type.String({ minLength: 1 }),
    endTime: Type.String({ minLength: 1 }),
    title: Type.Optional(Type.String()),
    appointmentStatus: Type.Optional(GHLAppointmentStatus),
    assignedUserId: Type.Optional(Type.String()),
    address: Type.Optional(Type.String()),
    notes: Type.Optional(Type.String()),
    source: Type.Optional(Type.String()),
    dateAdded: Type.Optional(Type.String()),
    dateUpdated: Type.Optional(Type.String()),
}, {
    additionalProperties: true,
    description: 'GoHighLevel Calendar Appointment — partial projection (SDR scope)',
});
//# sourceMappingURL=ghl-appointment.js.map